
// ─── API STORAGE SERVICE ──────────────────────────────────────────────────────
//
//  Replaces the old localStorage implementation with async fetch() calls to the
//  Express API server running at http://localhost:3001 (server/server.js).
//
//  Theme preference still uses localStorage — it is a pure UI setting that
//  does not need to be persisted on the server.
//
//  All methods return Promises.  Callers use async/await.
//

const API_BASE = '/api';  // same-origin: served by the Express server

// CSRF token for cookie-based sessions — fetched once on app load via fetchCsrfToken().
// Attached to all state-changing requests as X-CSRF-Token header.
let _csrfToken = null;

// In-memory store for verified timeline passwords (password-protected timelines).
// Keyed by timeline ID. Cleared on page unload automatically since it's in-memory.
const _tlPasswords = new Map();

const _fetch = async (method, path, body, _isRetry = false) => {
  const headers = { 'Content-Type': 'application/json' };

  // Attach CSRF token to all state-changing requests (cookie-based sessions).
  if (_csrfToken && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
    headers['X-CSRF-Token'] = _csrfToken;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    credentials: 'same-origin', // ensures cookies are sent with every request
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }));

    if (res.status === 401 && err.code === 'SESSION_EXPIRED') {
      localStorage.removeItem('chronos_token');
      window.dispatchEvent(new CustomEvent('chronos:session-expired'));
    }

    // CSRF token expired or rotated — re-fetch it and retry the request once.
    if (res.status === 403 && err.code === 'CSRF_INVALID' && !_isRetry) {
      try {
        const { csrfToken } = await fetch(`${API_BASE}/auth/csrf-token`, {
          credentials: 'same-origin',
        }).then(r => r.json());
        _csrfToken = csrfToken;
        return _fetch(method, path, body, true);
      } catch { /* fall through and throw the original error */ }
    }

    throw new Error(err.error || err.message || `HTTP ${res.status}`);
  }
  return res.json();
};

export const Storage = {

  // ── Image upload ────────────────────────────────────────────────────────────

  /**
   * Upload an image File to Supabase Storage via the server.
   * @param {File}   file  The File object from an <input type="file">
   * @param {string} type  One of: "avatar" | "background" | "event-photo"
   * @returns {Promise<string>} Public URL of the uploaded image
   */
  uploadImage: async (file, type) => {
    // Client-side size guard: warn at 10 MB, hard-reject at 20 MB (server also
    // enforces 20 MB — this just gives the user a faster, friendlier error).
    const WARN_BYTES = 10 * 1024 * 1024;
    const MAX_BYTES  = 20 * 1024 * 1024;
    if (file.size > MAX_BYTES) {
      throw new Error('Image must be smaller than 20MB');
    }
    if (file.size > WARN_BYTES) {
      // Dispatch a custom event so any mounted toast listener can surface the
      // warning without creating a hard dependency between storage.js and React.
      window.dispatchEvent(new CustomEvent('chronos:upload-warning', {
        detail: `Large image detected (${(file.size / 1024 / 1024).toFixed(1)} MB). We'll optimise it automatically — this may take a moment.`,
      }));
    }

    const formData = new FormData();
    formData.append('file', file);

    const uploadHeaders = {};
    if (_csrfToken) uploadHeaders['X-CSRF-Token'] = _csrfToken;
    // Do NOT set Content-Type manually — the browser adds the multipart boundary.

    const res = await fetch(`${API_BASE}/upload/${type}`, {
      method:      'POST',
      headers:     uploadHeaders,
      credentials: 'same-origin',
      body:        formData,
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(err.error || 'Upload failed');
    }

    const { url } = await res.json();
    return url;
  },

  // ── Auth ────────────────────────────────────────────────────────────────────

  signup: async (name, email, password, agreedToTerms) => {
    // Server returns { pending: true } — email verification required before login.
    await _fetch('POST', '/auth/signup', { name, email, password, createdAt: Date.now(), agreedToTerms: !!agreedToTerms });
  },

  resendVerification: async (email) => {
    await _fetch('POST', '/auth/resend-verification', { email });
  },

  forgotPassword: async (email) => {
    await _fetch('POST', '/auth/forgot-password', { email });
  },

  resetPassword: async (token, password) => {
    await _fetch('POST', '/auth/reset-password', { token, password });
  },

  login: async (email, password, rememberMe = false) => {
    const { user } = await _fetch('POST', '/auth/login', { email, password, rememberMe });
    // Remove the old localStorage token — the server now sets an HttpOnly cookie.
    // This completes the per-user migration on their first login after deployment.
    localStorage.removeItem('chronos_token');
    return user;
  },

  /** Validates the session (cookie or legacy Bearer).  Returns { user, expiresAt } or null. */
  getSession: async () => {
    try {
      const { user, expiresAt } = await _fetch('GET', '/auth/session');
      return { user, expiresAt };
    } catch {
      localStorage.removeItem('chronos_token');
      return null;
    }
  },

  /** Fetches the CSRF token for the current cookie session and stores it in memory. */
  fetchCsrfToken: async () => {
    try {
      const { csrfToken } = await _fetch('GET', '/auth/csrf-token');
      _csrfToken = csrfToken;
    } catch {
      _csrfToken = null;
    }
  },

  /** Returns all active sessions for the current user. */
  getSessions: async () => {
    const { sessions } = await _fetch('GET', '/auth/sessions');
    return sessions;
  },

  /** Revokes a specific session by its numeric ID. */
  revokeSession: async (sessionId) => {
    await _fetch('DELETE', `/auth/sessions/${sessionId}`);
  },

  /** Revokes all sessions except the current one. Requires the user's password. */
  revokeOtherSessions: async (password) => {
    await _fetch('DELETE', '/auth/sessions/others', { password });
  },

  clearSession: async () => {
    await _fetch('DELETE', '/auth/session').catch(() => {});
    localStorage.removeItem('chronos_token');
    _csrfToken = null;
  },

  // ── Theme (localStorage — pure UI preference) ──────────────────────────────

  getTheme:  () => localStorage.getItem('chronos_theme') || 'dark',
  saveTheme: (t) => localStorage.setItem('chronos_theme', t),

  // ── User ───────────────────────────────────────────────────────────────────

  markOnboarded: async (userId) => {
    await _fetch('PUT', `/users/${userId}/onboarded`);
  },

  updateUser: async (updatedUser) => {
    const { user } = await _fetch('PUT', `/users/${updatedUser.id}`, updatedUser);
    return user;
  },

  /**
   * Request an email address change.
   * Sends a verification link to the new address; the old email stays active
   * until the link is clicked.
   */
  requestEmailChange: async (userId, newEmail, currentPassword) => {
    await _fetch('PUT', `/users/${userId}/email`, { newEmail, currentPassword });
  },

  /**
   * Permanently delete the account and all associated data.
   * Clears the local session token on success.
   */
  deleteAccount: async (userId, password) => {
    await _fetch('DELETE', `/users/${userId}`, { password });
    localStorage.removeItem('chronos_token');
  },

  exportAccountData: async (userId) => {
    const headers = {};
    if (_csrfToken) headers['X-CSRF-Token'] = _csrfToken;
    const res = await fetch(`${API_BASE}/users/${userId}/export`, {
      method: 'GET', headers, credentials: 'same-origin',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Export failed' }));
      throw new Error(err.error || 'Export failed');
    }
    const blob  = await res.blob();
    const url   = URL.createObjectURL(blob);
    const a     = document.createElement('a');
    const today = new Date().toISOString().slice(0, 10);
    a.href      = url;
    a.download  = `chronos-data-export-${today}.json`;
    a.click();
    URL.revokeObjectURL(url);
  },

  // ── Timelines ──────────────────────────────────────────────────────────────

  getTimelines: async (userId) => {
    const { timelines } = await _fetch('GET', `/timelines?userId=${userId}`);
    return timelines;
  },

  createTimeline: async (timeline) => {
    const { timeline: created, isFirst } = await _fetch('POST', '/timelines', timeline);
    return { timeline: created, isFirst };
  },

  updateTimeline: async (timeline) => {
    const { timeline: updated } = await _fetch('PUT', `/timelines/${timeline.id}`, timeline);
    return updated;
  },

  deleteTimeline: async (id) => {
    await _fetch('DELETE', `/timelines/${id}`);
  },

  duplicateTimeline: async (id, newTitle) => {
    const { timeline } = await _fetch('POST', `/timelines/${id}/duplicate`, { title: newTitle });
    return timeline;
  },

  /** Throws if the password is wrong; resolves if correct. */
  verifyTimelinePassword: async (timelineId, password) => {
    await _fetch('POST', `/timelines/${timelineId}/verify-password`, { password });
  },

  // ── Social ─────────────────────────────────────────────────────────────────

  getSocial: async (timelineIds) => {
    const ids = Array.isArray(timelineIds) ? timelineIds : [timelineIds];
    const { social } = await _fetch('GET', `/social?timelineIds=${ids.join(',')}`);
    return social;
  },

  getSocialForTimeline: async (timelineId) => {
    const { social } = await _fetch('GET', `/social/${timelineId}`);
    return social;
  },

  updateSocial: async (timelineId, data) => {
    await _fetch('PUT', `/social/${timelineId}`, data);
  },

  // ── Notifications ──────────────────────────────────────────────────────────

  getNotifications: async ({ page = 1, limit = 20 } = {}) => {
    const params = new URLSearchParams({ page, limit });
    const { notifications, hasMore, totalCount } = await _fetch('GET', `/notifications?${params}`);
    return { notifications, hasMore, totalCount };
  },

  getUnreadCount: async () => {
    const { count } = await _fetch('GET', '/notifications/unread-count');
    return count;
  },

  markNotificationsRead: async () => {
    await _fetch('PUT', '/notifications/read');
  },

  // ── Follows ────────────────────────────────────────────────────────────────

  follow: async (userId) => {
    await _fetch('POST', `/users/${userId}/follow`);
  },

  unfollow: async (userId) => {
    await _fetch('DELETE', `/users/${userId}/follow`);
  },

  getFollowers: async (userId) => {
    const { followers, count } = await _fetch('GET', `/users/${userId}/followers`);
    return { followers, count };
  },

  getFollowing: async (userId) => {
    const { following, count } = await _fetch('GET', `/users/${userId}/following`);
    return { following, count };
  },

  getPublicProfile: async (userId) => {
    return await _fetch('GET', `/users/${userId}/public-profile`);
  },

  // ── Feed ───────────────────────────────────────────────────────────────────

  /**
   * Unified paginated feed fetch.
   * Handles all filter/search combinations in a single call.
   *
   * @param {object} opts
   * @param {number} opts.page       - 1-based page number (default 1)
   * @param {number} opts.limit      - results per page (default 10)
   * @param {string} opts.filter     - 'all' | 'following' (default 'all')
   * @param {string} opts.category   - exact category or '' for all
   * @param {string} opts.keyword    - free-text search string
   * @returns {Promise<{ posts, hasMore, totalCount }>}
   */
  getFeed: async ({ page = 1, limit = 10, filter = 'all', category = '', orientation = '', keyword = '', sort = 'recent', scope = 'timelines' } = {}) => {
    const params = new URLSearchParams();
    params.set('page',  page);
    params.set('limit', limit);
    if (filter && filter !== 'all')     params.set('filter',      filter);
    if (category)                       params.set('category',    category);
    if (orientation)                    params.set('orientation', orientation);
    if (keyword)                        params.set('keyword',     keyword);
    if (sort && sort !== 'recent')      params.set('sort',        sort);
    if (scope && scope !== 'timelines') params.set('scope',       scope);
    return _fetch('GET', `/feed?${params}`);
    // Returns { posts, hasMore, totalCount }
  },

  getPublicTimeline: async (timelineId, password = null) => {
    const pw = password ?? _tlPasswords.get(timelineId) ?? null;
    const headers = { 'Content-Type': 'application/json' };
    if (pw) headers['X-Timeline-Password'] = pw;
    const res = await fetch(`${API_BASE}/feed/timeline/${timelineId}`, {
      method: 'GET', headers, credentials: 'same-origin',
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: 'Request failed' }));
      if (res.status === 403) {
        const e = new Error(body.wrongPassword ? 'Incorrect password.' : 'Password required');
        e.status           = 403;
        e.requiresPassword = true;
        e.wrongPassword    = !!body.wrongPassword;
        e.isPrivate        = !!body.isPrivate;
        e.tl               = body.tl ?? null;
        throw e;
      }
      const e = new Error(body.error || 'Request failed');
      e.status = res.status;
      throw e;
    }
    const data = await res.json();
    if (pw) _tlPasswords.set(timelineId, pw);
    return data;
  },

  storeTimelinePassword: (timelineId, password) => {
    if (password) _tlPasswords.set(timelineId, password);
    else _tlPasswords.delete(timelineId);
  },

  // Alias used by TimelineEmbed — returns the same shape as getPublicTimeline
  getTimelineForEmbed: async (timelineId) => {
    const { tl, author } = await _fetch('GET', `/feed/timeline/${timelineId}`);
    return { timeline: tl, author };
  },

  // ── Version history ────────────────────────────────────────────────────────

  /** Returns summary list (no full event data) for the version history panel. */
  getVersions: async (timelineId) => {
    const { versions } = await _fetch('GET', `/timelines/${timelineId}/versions`);
    return { versions };
  },

  /** Returns full version data (events, colors, etc.) for preview. */
  getVersion: async (timelineId, versionId) => {
    const { version } = await _fetch('GET', `/timelines/${timelineId}/versions/${versionId}`);
    return { version };
  },

  /**
   * Snapshots the current timeline state, then restores the given version.
   * @param {string} timelineId
   * @param {string} versionId
   * @param {string|null} label  Optional label for the pre-restore snapshot.
   * @returns {Promise<object>} The restored timeline object.
   */
  restoreVersion: async (timelineId, versionId, label = null) => {
    const { timeline } = await _fetch('POST', `/timelines/${timelineId}/versions/${versionId}/restore`, { label });
    return timeline;
  },

  /** Sets or clears the label on a version. Pass null or '' to remove. */
  labelVersion: async (timelineId, versionId, label) => {
    const { label: saved } = await _fetch('PUT', `/timelines/${timelineId}/versions/${versionId}/label`, { label });
    return saved;
  },

  searchFeed: async ({ keyword = '', category = '', page = 1, limit = 10 } = {}) => {
    const params = new URLSearchParams();
    if (keyword)  params.set('keyword',  keyword);
    if (category) params.set('category', category);
    params.set('page',  page);
    params.set('limit', limit);
    return _fetch('GET', `/feed/search?${params}`);
    // Returns { posts, hasMore, totalCount }
  },

  // ── Collaboration ──────────────────────────────────────────────────────────

  /**
   * Returns timelines shared with the current user as a collaborator.
   * Each entry includes { ...timeline, collaboratorRole, owner: { id, name, avatar } }.
   */
  getSharedTimelines: async () => {
    const { timelines } = await _fetch('GET', '/timelines/shared');
    return timelines;
  },

  /**
   * Returns accepted collaborators and pending invites for a timeline.
   * Owner only.
   * @returns {{ collaborators: Collaborator[], pendingInvites: PendingInvite[] }}
   */
  getCollaborators: async (timelineId) => {
    return _fetch('GET', `/timelines/${timelineId}/collaborators`);
    // Returns { collaborators, pendingInvites }
  },

  /**
   * Send a collaboration invite email.
   * @param {string} timelineId
   * @param {string} email
   * @param {'viewer'|'editor'} role
   */
  inviteCollaborator: async (timelineId, email, role) => {
    await _fetch('POST', `/timelines/${timelineId}/collaborators/invite`, { email, role });
  },

  /**
   * Change an accepted collaborator's role.  Owner only.
   * @param {'viewer'|'editor'} role
   */
  updateCollaboratorRole: async (timelineId, userId, role) => {
    await _fetch('PUT', `/timelines/${timelineId}/collaborators/${userId}`, { role });
  },

  /**
   * Cancel a pending invite before it is accepted.  Owner only.
   */
  cancelInvite: async (timelineId, inviteId) => {
    await _fetch('DELETE', `/timelines/${timelineId}/collaborators/invites/${inviteId}`);
  },

  /**
   * Remove an accepted collaborator.
   * Owners can remove anyone; editors/viewers can only remove themselves.
   */
  removeCollaborator: async (timelineId, userId) => {
    await _fetch('DELETE', `/timelines/${timelineId}/collaborators/${userId}`);
  },

  /**
   * Transfer timeline ownership to an accepted collaborator.
   * Requires the current owner's password for confirmation.
   * After transfer the current user becomes an editor.
   */
  transferOwnership: async (timelineId, userId, password) => {
    await _fetch('POST', `/timelines/${timelineId}/collaborators/${userId}/transfer-ownership`, { password });
  },

  /**
   * Initiate a token-based ownership transfer to an accepted collaborator.
   * Sends an email to the recipient with Accept/Decline links.
   * @param {string} timelineId
   * @param {string} toUserId  — must be an accepted collaborator (editor or viewer)
   * @param {string} [reason]  — optional message shown in the email
   */
  initiateOwnershipTransfer: async (timelineId, toUserId, reason) => {
    return _fetch('POST', `/timelines/${timelineId}/transfer`, { toUserId, reason });
  },

  /**
   * Cancel a pending ownership transfer (owner only).
   * Notifies the recipient that the transfer was cancelled.
   */
  cancelOwnershipTransfer: async (timelineId) => {
    return _fetch('DELETE', `/timelines/${timelineId}/transfer`);
  },

  /**
   * Accept an ownership transfer using the token from the email link.
   * @param {string} token  — 64-char hex token from URL param
   * @returns {{ ok, timelineId, timelineTitle }}
   */
  acceptTransfer: async (token) => {
    return _fetch('POST', '/timelines/transfer/accept', { token });
  },

  /**
   * Decline an ownership transfer using the token from the email link.
   * @param {string} token  — 64-char hex token from URL param
   */
  declineTransfer: async (token) => {
    return _fetch('POST', '/timelines/transfer/decline', { token });
  },

  // ── Collaboration activity log ─────────────────────────────────────────────

  /**
   * Fetch the collaboration activity log for a timeline.
   * @param {string} timelineId
   * @param {{ limit?: number, before?: number }} [opts]  before = created_at cursor for pagination
   * @returns {{ activity: ActivityItem[], hasMore: boolean }}
   */
  getActivity: async (timelineId, { limit = 25, before } = {}) => {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (before) qs.set('before', String(before));
    return _fetch('GET', `/timelines/${timelineId}/activity?${qs}`);
  },

  // ── Collaboration discussion threads ──────────────────────────────────────

  /**
   * Fetch all discussion comments (with nested replies) for a timeline.
   * @returns {{ comments: DiscussionComment[] }}
   */
  getDiscussions: async (timelineId) => {
    return _fetch('GET', `/timelines/${timelineId}/discussions`);
  },

  /**
   * Post a new top-level discussion comment.
   * @param {string} body  — plain text, max 2000 chars
   * @returns {{ comment: DiscussionComment }}
   */
  postDiscussionComment: async (timelineId, body) => {
    return _fetch('POST', `/timelines/${timelineId}/discussions`, { body });
  },

  /**
   * Post a reply to an existing top-level discussion comment.
   * @returns {{ reply: DiscussionReply }}
   */
  postDiscussionReply: async (timelineId, commentId, body) => {
    return _fetch('POST', `/timelines/${timelineId}/discussions/${commentId}/replies`, { body });
  },

  /**
   * Delete a top-level discussion comment.
   * Soft-deletes if the comment has replies; hard-deletes otherwise.
   * @returns {{ ok: true, softDeleted: boolean }}
   */
  deleteDiscussionComment: async (timelineId, commentId) => {
    return _fetch('DELETE', `/timelines/${timelineId}/discussions/${commentId}`);
  },

  /**
   * Delete a reply to a discussion comment.
   */
  deleteDiscussionReply: async (timelineId, commentId, replyId) => {
    return _fetch('DELETE', `/timelines/${timelineId}/discussions/${commentId}/replies/${replyId}`);
  },

  // ── Invites ────────────────────────────────────────────────────────────────

  /**
   * Fetch invite metadata by token (public — no auth required).
   * Used to display invite details before the user logs in.
   * @returns {{ timelineId, timelineTitle, inviterName, email, role, expiresAt }}
   */
  getInvite: async (token) => {
    return _fetch('GET', `/invites/${token}`);
  },

  /**
   * Accept a collaboration invite.  Requires the user to be signed in.
   * @returns {{ ok, timelineId, role }}
   */
  acceptInvite: async (token) => {
    return _fetch('POST', `/invites/${token}/accept`);
  },

  /**
   * Decline (expire) an invite.  No auth required.
   */
  declineInvite: async (token) => {
    await _fetch('DELETE', `/invites/${token}`);
  },

  // ── Presence ───────────────────────────────────────────────────────────────

  /**
   * Send an editor-presence heartbeat.  Call every 30 s while the editor is open.
   * @returns {{ others: Array<{ userId, name, avatar }> }}
   *   Users (other than the caller) who are currently editing the same timeline.
   */
  heartbeat: async (timelineId) => {
    return _fetch('POST', `/timelines/${timelineId}/heartbeat`);
  },

  // ── Admin ──────────────────────────────────────────────────────────────────
  //  All endpoints require is_admin = 1.  Non-admins receive 403.

  /** Platform-wide counts: users, timelines, comments, likes, reports, etc. */
  adminGetStats: () => _fetch('GET', '/admin/stats'),

  /**
   * Paginated user list with optional search and filter.
   * @param {{ page?, search?, filter? }} opts  filter: 'all'|'admin'|'banned'
   * @returns {{ users, total, page, pages }}
   */
  adminGetUsers: (opts = {}) => {
    const q = new URLSearchParams();
    if (opts.page)   q.set('page',   opts.page);
    if (opts.search) q.set('search', opts.search);
    if (opts.filter) q.set('filter', opts.filter);
    const qs = q.toString();
    return _fetch('GET', `/admin/users${qs ? `?${qs}` : ''}`);
  },

  /** Full user detail: profile, timelines, recent comments, moderation history. */
  adminGetUser: (userId) => _fetch('GET', `/admin/users/${userId}`),

  /**
   * Ban a user.  Immediately invalidates all their sessions.
   * @param {string} reason  Required explanation logged to moderation_log.
   */
  adminBanUser: (userId, reason) => _fetch('POST', `/admin/users/${userId}/ban`, { reason }),

  /** Lift a ban from a user. */
  adminUnbanUser: (userId) => _fetch('POST', `/admin/users/${userId}/unban`, {}),

  /**
   * Delete a timeline (cascades to comments, replies, likes, etc.).
   * @param {string} reason  Required — logged to moderation_log.
   */
  adminDeleteTimeline: (timelineId, reason) =>
    _fetch('DELETE', `/admin/timelines/${timelineId}`, { reason }),

  /**
   * Delete a comment or reply by ID (server checks both tables).
   * @param {string} reason  Required — logged to moderation_log.
   */
  adminDeleteComment: (commentId, reason) =>
    _fetch('DELETE', `/admin/comments/${commentId}`, { reason }),

  /**
   * Paginated timeline list for the Content moderation tab.
   * @param {{ page?, search? }} opts
   * @returns {{ timelines, total, page, pages }}
   */
  adminGetTimelines: (opts = {}) => {
    const q = new URLSearchParams();
    if (opts.page)   q.set('page',   opts.page);
    if (opts.search) q.set('search', opts.search);
    const qs = q.toString();
    return _fetch('GET', `/admin/timelines${qs ? `?${qs}` : ''}`);
  },

  /**
   * Paginated moderation log.
   * @param {{ page?, action?, since? }} opts  since: ISO date string
   * @returns {{ log, total, page, pages }}
   */
  adminGetLogs: (opts = {}) => {
    const q = new URLSearchParams();
    if (opts.page)   q.set('page',   opts.page);
    if (opts.action) q.set('action', opts.action);
    if (opts.since)  q.set('since',  opts.since);
    const qs = q.toString();
    return _fetch('GET', `/admin/moderation-log${qs ? `?${qs}` : ''}`);
  },

  /**
   * Search users and timelines simultaneously.
   * @returns {{ users: User[], timelines: Timeline[] }}  Up to 10 each.
   */
  adminSearch: (q) => _fetch('GET', `/admin/search?q=${encodeURIComponent(q)}`),

  /**
   * Paginated comments + replies for content moderation.
   * @param {{ page?, search? }} opts
   * @returns {{ comments, total, page, pages }}
   */
  adminGetComments: (opts = {}) => {
    const q = new URLSearchParams();
    if (opts.page)   q.set('page',   opts.page);
    if (opts.search) q.set('search', opts.search);
    const qs = q.toString();
    return _fetch('GET', `/admin/comments${qs ? `?${qs}` : ''}`);
  },

  /**
   * Issue a formal warning to a user (logged to moderation_log, no account action).
   * @param {string} reason  Required.
   */
  adminWarnUser: (userId, reason) => _fetch('POST', `/admin/users/${userId}/warn`, { reason }),

  // ── Report / Flag system ────────────────────────────────────────────────────

  // ── Comment & reply likes ──────────────────────────────────────────────────

  likeComment: async (commentId) => {
    const { likeCount } = await _fetch('POST', `/comments/${commentId}/like`);
    return likeCount;
  },

  unlikeComment: async (commentId) => {
    const { likeCount } = await _fetch('DELETE', `/comments/${commentId}/like`);
    return likeCount;
  },

  getCommentLikers: async (commentId) => {
    const { likers } = await _fetch('GET', `/comments/${commentId}/likes`);
    return likers;
  },

  likeReply: async (replyId) => {
    const { likeCount } = await _fetch('POST', `/replies/${replyId}/like`);
    return likeCount;
  },

  unlikeReply: async (replyId) => {
    const { likeCount } = await _fetch('DELETE', `/replies/${replyId}/like`);
    return likeCount;
  },

  getReplyLikers: async (replyId) => {
    const { likers } = await _fetch('GET', `/replies/${replyId}/likes`);
    return likers;
  },

  // ── @Mention user search ────────────────────────────────────────────────────

  searchUsers: async (query) => {
    const { users } = await _fetch('GET', `/users/search?q=${encodeURIComponent(query)}`);
    return users;
  },

  // ── Blocking ────────────────────────────────────────────────────────────────

  blockUser: async (userId) => {
    await _fetch('POST', `/users/${userId}/block`);
  },

  unblockUser: async (userId) => {
    await _fetch('DELETE', `/users/${userId}/block`);
  },

  getBlockedUsers: async ({ page = 1, limit = 20 } = {}) => {
    const { blocked } = await _fetch('GET', `/users/blocked?page=${page}&limit=${limit}`);
    return blocked;
  },

  checkIsBlocked: async (userId) => {
    return _fetch('GET', `/users/${userId}/is-blocked`);
  },

  // ── Bookmarks ────────────────────────────────────────────────────────────────

  addBookmark: async (timelineId, note = null) => {
    return _fetch('POST', '/bookmarks', { timeline_id: timelineId, note });
    // Returns { ok, bookmarkCount }
  },

  removeBookmark: async (timelineId) => {
    return _fetch('DELETE', `/bookmarks/${timelineId}`);
    // Returns { ok, bookmarkCount }
  },

  updateBookmarkNote: async (timelineId, note) => {
    return _fetch('PUT', `/bookmarks/${timelineId}`, { note });
    // Returns { ok, note }
  },

  getBookmarks: async ({ page = 1, limit = 20 } = {}) => {
    const params = new URLSearchParams({ page, limit });
    return _fetch('GET', `/bookmarks?${params}`);
    // Returns { bookmarks, hasMore, totalCount }
  },

  getRecommendations: async (timelineId, limit = 6) => {
    const params = new URLSearchParams({ limit });
    return _fetch('GET', `/timelines/${timelineId}/recommendations?${params}`);
    // Returns { recommendations: [{ tl, author, likeCount }] }
  },

  // ── Forking ─────────────────────────────────────────────────────────────────

  forkTimeline: (timelineId) =>
    _fetch('POST', `/timelines/${timelineId}/fork`),
  // Returns { timeline } — the new forked timeline owned by the current user

  // ── CSV Import ──────────────────────────────────────────────────────────────

  /**
   * Upload a CSV file and import its events into a timeline.
   * Uses raw fetch (multipart) so the CSRF token is handled manually.
   * @param {string}   timelineId
   * @param {FormData} formData   must contain field "file" with the CSV File
   * @returns {Promise<{ imported, skipped, errors, warnings, snapshotVersionId, totalEvents }>}
   */
  importEventsFromCSV: async (timelineId, formData) => {
    const headers = {};
    if (_csrfToken) headers['X-CSRF-Token'] = _csrfToken;

    const res = await fetch(`${API_BASE}/timelines/${timelineId}/import/csv`, {
      method:      'POST',
      headers,
      credentials: 'same-origin',
      body:        formData,
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Import failed' }));
      throw new Error(err.error || 'Import failed');
    }
    return res.json();
  },

  /**
   * Triggers a browser download of the CSV import template.
   * Purely client-side — no network request.
   *
   * @param {string} [layout]  'map' renders a map-flavored template with
   *                           lat/lng/location/mapIcon/mapMarkerSize/photo
   *                           columns and Roman-Empire sample rows.  Any
   *                           other value falls back to the classic template.
   */
  downloadCSVTemplate: (layout) => {
    let header, body, filename;
    if (layout === 'map') {
      header = 'title,startDate,endDate,description,lat,lng,location,mapIcon,mapMarkerSize,tags,photo';
      // Two realistic Roman-Empire example rows showing every supported field.
      body = [
        '"Founding of Rome","-0753-04-21","","Traditional date of Rome\'s founding by Romulus.","41.8902","12.4922","Rome, Italia","crown","large","founding,legend",""',
        '"Battle of Actium","-0031-09-02","","Decisive naval battle that ended the Roman Republic.","38.9500","20.7333","Actium, Greece","ship","medium","battle,naval,augustus",""',
      ].join('\n');
      filename = 'chronos_map_template.csv';
    } else {
      header = 'title,startYear,startMonth,startDay,startEra,endYear,endMonth,endDay,endEra,description,category,color,tags,videoUrl';
      body   = '"Battle of Hastings","1066","10","14","AD","","","","","Norman invasion of England","History","#e74c3c","medieval war conquest",""';
      filename = 'chronos_import_template.csv';
    }
    const blob = new Blob([`${header}\n${body}\n`], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  // ── GEDCOM Import ───────────────────────────────────────────────────────────

  importEventsFromGEDCOM: async (timelineId, formData) => {
    const headers = {};
    if (_csrfToken) headers['X-CSRF-Token'] = _csrfToken;

    const res = await fetch(`${API_BASE}/timelines/${timelineId}/import/gedcom`, {
      method:      'POST',
      headers,
      credentials: 'same-origin',
      body:        formData,
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Import failed' }));
      throw new Error(err.error || 'Import failed');
    }
    return res.json();
  },

  // ── CSV Export (client-side) ────────────────────────────────────────────────

  exportTimelineCSV: (timeline) => {
    // Decode a Chronos dateKey → { year, month, day, era } (mirrors fromDateKey in utils.js)
    const decodeKey = (key) => {
      if (!key) return null;
      const biased = parseInt(key.slice(0, 6), 10);
      const month  = parseInt(key.slice(7, 9),  10);
      const day    = parseInt(key.slice(10, 12), 10);
      const astro  = biased - 99999;
      const era    = astro >= 1 ? 'AD' : 'BC';
      const year   = astro >= 1 ? astro : 1 - astro;
      return { year, month: month || 0, day: day || 0, era };
    };

    // Wrap a cell value in quotes, escaping any embedded quotes
    const cell = (v) => {
      const s = (v == null ? '' : String(v)).replace(/"/g, '""');
      return `"${s}"`;
    };

    const headers = [
      'title','startYear','startMonth','startDay','startEra',
      'endYear','endMonth','endDay','endEra',
      'description','category','color','tags','videoUrl',
    ];

    const rows = [headers.join(',')];

    for (const ev of timeline.events) {
      const s = decodeKey(ev.startDate);
      const e = decodeKey(ev.endDate);
      const desc = (ev.description || '').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
      const tags = Array.isArray(ev.tags) ? ev.tags.join(' ') : '';

      rows.push([
        cell(ev.title),
        s ? s.year  : '', s ? (s.month || '') : '', s ? (s.day || '') : '', s ? s.era : '',
        e ? e.year  : '', e ? (e.month || '') : '', e ? (e.day || '') : '', e ? e.era : '',
        cell(desc),
        cell(ev.category || ''),
        cell(ev.color    || ''),
        cell(tags),
        cell(ev.videoUrl || ''),
      ].join(','));
    }

    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${(timeline.title || 'timeline').replace(/[^a-z0-9_-]/gi, '_').slice(0, 60)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },

  // ── JSON Export (client-side) ────────────────────────────────────────────────

  exportTimelineJSON: (timeline) => {
    const payload = {
      chronosExport: true,
      version:       '1.0',
      exportedAt:    new Date().toISOString(),
      timeline: {
        title:       timeline.title,
        description: timeline.description,
        orientation: timeline.orientation,
        category:    timeline.category,
        events:      timeline.events,
        colors:      timeline.colors,
        bgImage:     timeline.bgImage,
        manualOrder: timeline.manualOrder,
        chapters:    timeline.chapters,
      },
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${(timeline.title || 'timeline').replace(/[^a-z0-9_-]/gi, '_').slice(0, 60)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  },

  // ── iCal Export ─────────────────────────────────────────────────────────────

  exportIcal: async (timelineId, title = 'timeline') => {
    const headers = {};

    const res = await fetch(`${API_BASE}/timelines/${timelineId}/export/ical`, {
      method:      'GET',
      headers,
      credentials: 'same-origin',
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Export failed' }));
      throw new Error(err.error || 'Export failed');
    }

    const blob     = await res.blob();
    const url      = URL.createObjectURL(blob);
    const a        = document.createElement('a');
    a.href         = url;
    a.download     = `${title.replace(/[^a-z0-9_-]/gi, '_').slice(0, 60)}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  },

  // ── Carousel Export ─────────────────────────────────────────────────────────

  exportCarousel: async (timelineId, title = 'timeline', eventIds = null) => {
    const doRequest = async () => {
      const headers = { 'Content-Type': 'application/json' };
      if (_csrfToken) headers['X-CSRF-Token'] = _csrfToken;
      const body = eventIds ? { eventIds } : {};
      return fetch(`${API_BASE}/timelines/${timelineId}/export/carousel`, {
        method: 'POST', headers, credentials: 'same-origin', body: JSON.stringify(body),
      });
    };

    let res = await doRequest();

    // CSRF expired — refresh token and retry once (mirrors _fetch logic)
    if (res.status === 403) {
      try {
        const errData = await res.clone().json().catch(() => ({}));
        if (errData.code === 'CSRF_INVALID') {
          const { csrfToken } = await fetch(`${API_BASE}/auth/csrf-token`, {
            credentials: 'same-origin',
          }).then(r => r.json());
          _csrfToken = csrfToken;
          res = await doRequest();
        }
      } catch {}
    }

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      let errMsg = 'Carousel export failed';
      try {
        errMsg = JSON.parse(text).error || errMsg;
      } catch {
        if (text) errMsg = text.slice(0, 120);
      }
      throw new Error(errMsg);
    }

    const blob      = await res.blob();
    const url       = URL.createObjectURL(blob);
    const a         = document.createElement('a');
    const safeTitle = title.replace(/[^a-z0-9_-]/gi, '_').slice(0, 40);
    a.href          = url;
    a.download      = `${safeTitle}-carousel.zip`;
    a.click();
    URL.revokeObjectURL(url);
  },

  // ── Event tags ──────────────────────────────────────────────────────────────

  getTags: (q = '') => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    return _fetch('GET', `/tags?${params}`);
  },
  // Returns { tags: string[] }

  // ── Templates ───────────────────────────────────────────────────────────────

  getTemplates: () => _fetch('GET', '/templates'),
  // Returns { templates: [{ id, name, description, category, orientation, previewColor, isFeatured, useCount }] }

  getTemplate: (id) => _fetch('GET', `/templates/${id}`),
  // Returns full template including timelineData

  searchTemplates: (query = '', category = '') => {
    const params = new URLSearchParams();
    if (query)    params.set('q',        query);
    if (category) params.set('category', category);
    return _fetch('GET', `/templates/search?${params}`);
  },

  createFromTemplate: (templateId) =>
    _fetch('POST', `/timelines/from-template/${templateId}`),
  // Returns { timeline, isFirst, templateId, templateName }

  // ── Admin templates ──────────────────────────────────────────────────────────

  adminGetTemplates: () => _fetch('GET', '/admin/templates'),

  adminGetTemplateFull: (id) => _fetch('GET', `/admin/templates/${id}/full`),

  adminCreateTemplate: (data) => _fetch('POST', '/admin/templates', data),

  adminUpdateTemplate: (id, data) => _fetch('PUT', `/admin/templates/${id}`, data),

  adminDeleteTemplate: (id) => _fetch('DELETE', `/admin/templates/${id}`),

  adminDuplicateTemplate: (id) => _fetch('POST', `/admin/templates/${id}/duplicate`),

  adminToggleTemplateFeatured: (id, isFeatured) =>
    _fetch('PATCH', `/admin/templates/${id}/featured`, { isFeatured }),

  submitReport: ({ contentType, contentId, reason, details }) =>
    _fetch('POST', '/reports', { contentType, contentId, reason, details }),

  adminGetReports: (opts = {}) => {
    const q = new URLSearchParams();
    if (opts.page)         q.set('page',         opts.page);
    if (opts.status)       q.set('status',       opts.status);
    if (opts.contentType)  q.set('content_type', opts.contentType);
    const qs = q.toString();
    return _fetch('GET', `/admin/reports${qs ? `?${qs}` : ''}`);
  },

  adminGetReportPendingCount: () => _fetch('GET', '/admin/reports/pending-count'),

  adminGetReport: (id) => _fetch('GET', `/admin/reports/${id}`),

  adminUpdateReport: (id, { status, adminNote }) =>
    _fetch('PUT', `/admin/reports/${id}`, { status, adminNote }),

  // ── Admin bulk actions ────────────────────────────────────────────────────────

  adminBulkBanUsers: (userIds, reason) =>
    _fetch('POST', '/admin/users/bulk-ban', { userIds, reason }),

  adminBulkWarnUsers: (userIds, reason) =>
    _fetch('POST', '/admin/users/bulk-warn', { userIds, reason }),

  adminBulkDeleteTimelines: (timelineIds, reason) =>
    _fetch('DELETE', '/admin/timelines/bulk', { timelineIds, reason }),

  adminBulkDeleteComments: (commentIds, replyIds, reason) =>
    _fetch('DELETE', '/admin/comments/bulk', { commentIds, replyIds, reason }),

  // ── IP ban management ─────────────────────────────────────────────────────────

  adminGetIpBans: (page = 1) => _fetch('GET', `/admin/ip-bans?page=${page}`),

  adminCreateIpBan: (ipAddress, banType, reason, expiresAt) =>
    _fetch('POST', '/admin/ip-bans', { ipAddress, banType, reason, expiresAt }),

  adminDeleteIpBan: (id) => _fetch('DELETE', `/admin/ip-bans/${id}`),

  adminBanUserIp: (userId) => _fetch('POST', `/admin/users/${userId}/ban-ip`),

  // ── Mod log export ────────────────────────────────────────────────────────────

  adminExportModLog: async () => {
    const headers = {};
    if (_csrfToken) headers['X-CSRF-Token'] = _csrfToken;
    const res = await fetch(`${API_BASE}/admin/logs/export`, { headers, credentials: 'same-origin' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Export failed' }));
      throw new Error(err.error || 'Export failed');
    }
    return res.text();
  },

  // ── Admin activity feed ───────────────────────────────────────────────────────

  adminGetActivity: () => _fetch('GET', '/admin/activity'),

  // ── Backup management ─────────────────────────────────────────────────────────

  triggerManualBackup: () => _fetch('POST', '/admin/backup'),

  getBackupStatus: () => _fetch('GET', '/admin/backup/status'),

  // ── Consent preferences ───────────────────────────────────────────────────────

  updateConsentPreferences: (consent) =>
    _fetch('POST', '/user/consent', { consent }),

  // ── Collections ──────────────────────────────────────────────────────────────

  getCollections: (userId) =>
    _fetch('GET', `/collections?userId=${encodeURIComponent(userId)}`),
  // Returns { collections: [{ id, userId, title, description, timelineCount, isOwner, createdAt, updatedAt }] }

  getCollection: (id) => _fetch('GET', `/collections/${id}`),
  // Returns { collection: { ...fields, timelines: [...], owner: { id, name, avatar } } }

  createCollection: (data) => _fetch('POST', '/collections', data),
  // Body: { title, description? } → { collection }

  updateCollection: (id, data) => _fetch('PUT', `/collections/${id}`, data),
  // Body: { title, description? } → { collection }

  deleteCollection: (id) => _fetch('DELETE', `/collections/${id}`),

  addToCollection: (collectionId, timelineId) =>
    _fetch('POST', `/collections/${collectionId}/timelines`, { timelineId }),

  removeFromCollection: (collectionId, timelineId) =>
    _fetch('DELETE', `/collections/${collectionId}/timelines/${timelineId}`),

  reorderCollection: (collectionId, timelineIds) =>
    _fetch('PUT', `/collections/${collectionId}/order`, { timelineIds }),

  // ── Dev Blog ─────────────────────────────────────────────────────────────────

  getDevBlogPosts: ({ page = 1 } = {}) =>
    _fetch('GET', `/dev-blog?page=${page}`),

  getDevBlogPost: (id) =>
    _fetch('GET', `/dev-blog/${id}`),

  getAdminDevBlogPosts: () =>
    _fetch('GET', '/admin/dev-blog'),

  createDevBlogPost: (data) =>
    _fetch('POST', '/dev-blog', data),

  updateDevBlogPost: (id, data) =>
    _fetch('PUT', `/dev-blog/${id}`, data),

  deleteDevBlogPost: (id) =>
    _fetch('DELETE', `/dev-blog/${id}`),

  toggleDevBlogPublish: (id) =>
    _fetch('PUT', `/dev-blog/${id}/publish`),

  uploadDevBlogCover: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const headers = {};
    if (_csrfToken) headers['X-CSRF-Token'] = _csrfToken;
    const res = await fetch(`${API_BASE}/upload/dev-blog-cover`, {
      method: 'POST', headers, credentials: 'same-origin', body: formData,
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Upload failed'); }
    return res.json(); // { url }
  },

  // ── Feedback ────────────────────────────────────────────────────────────────
  submitFeedback: ({ type, subject, body, email }) =>
    _fetch('POST', '/feedback', { type, subject, body, email }),
  adminListFeedback: ({ type, status, q, limit, offset } = {}) => {
    const params = new URLSearchParams();
    if (type)   params.set('type',   type);
    if (status) params.set('status', status);
    if (q)      params.set('q',      q);
    if (limit !== undefined)  params.set('limit',  String(limit));
    if (offset !== undefined) params.set('offset', String(offset));
    const qs = params.toString();
    return _fetch('GET', `/admin/feedback${qs ? `?${qs}` : ''}`);
  },
  adminUpdateFeedback: (id, patch)    => _fetch('PUT',    `/admin/feedback/${id}`, patch),
  adminMarkAllFeedbackRead: ()         => _fetch('POST',   '/admin/feedback/mark-all-read'),
  adminDeleteFeedback:  (id)          => _fetch('DELETE', `/admin/feedback/${id}`),
};
